"use client"

import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { AlertTriangle, PackageCheck, RefreshCw, Route } from "lucide-react"

import DriverScreen from "../components/DriverScreen.jsx"
import StopCard from "../components/StopCard.jsx"
import useDriverPlan from "../hooks/useDriverPlan.js"
import styles from "../css/driver.module.css"

export default function DriverDashboard() {
  const navigate = useNavigate()
  const { stops, currentPaymentId, branch, loading, refreshing, error, refresh } = useDriverPlan()

  const ordered = useMemo(
    () => [...stops].sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0)),
    [stops],
  )

  const statusFor = (stop) => {
    if (stop.stopStatus === "failed") return "failed"
    if (stop.status === "COMPLETED") return "done"
    if (currentPaymentId != null && Number(stop.paymentid) === currentPaymentId) return "current"
    return stop.canStart ? "current" : "locked"
  }

  const completedCount = ordered.filter((s) => statusFor(s) === "done").length
  const failedCount = ordered.filter((s) => statusFor(s) === "failed").length
  const total = ordered.length

  // Failed stops are resolved: they count as progress through the day even
  // though they weren't delivered, otherwise the bar can never reach the end.
  const resolvedCount = completedCount + failedCount
  const progressPercent = total > 0 ? Math.round((resolvedCount / total) * 100) : 0

  const remainingEta = ordered
    .filter((s) => !["done", "failed"].includes(statusFor(s)))
    .reduce((sum, s) => sum + (Number.isFinite(Number(s.etaMin)) ? Number(s.etaMin) : 0), 0)

  const openStop = (stop) => {
    const isDone = stop.status === "COMPLETED"
    navigate(isDone ? `/driver/completed/${stop.paymentid}` : `/driver/route/${stop.paymentid}`)
  }

  return (
    <DriverScreen>
      <div className={styles.progressCard}>
        <div className={styles.progressTop}>
          <div>
            <h1 className={styles.screenTitle}>Today&apos;s route</h1>
            {branch?.name && <div className={styles.stopRef}>{branch.name}</div>}
          </div>
          <div className={styles.progressCount}>
            {completedCount}
            <small> / {total || "—"}</small>
          </div>
        </div>

        <div
          className={styles.progressBar}
          role="progressbar"
          aria-valuenow={completedCount}
          aria-valuemin={0}
          aria-valuemax={total}
          aria-label="Deliveries completed"
        >
          <div className={styles.progressFill} style={{ width: `${progressPercent}%` }} />
        </div>

        <div className={styles.progressMeta}>
          <span>
            <PackageCheck size={14} aria-hidden="true" /> {total - resolvedCount} remaining
          </span>
          {failedCount > 0 && (
            <span>
              <AlertTriangle size={14} aria-hidden="true" /> {failedCount} not delivered
            </span>
          )}
          {remainingEta > 0 && (
            <span>
              <Route size={14} aria-hidden="true" /> ~{remainingEta} min driving left
            </span>
          )}
          {refreshing && <span>Refreshing…</span>}
        </div>
      </div>

      {loading && <div className={styles.statusMessage}>Loading today&apos;s route…</div>}

      {!loading && error && (
        <>
          <div className={styles.errorMessage}>{error}</div>
          <button type="button" className={styles.secondaryBtn} onClick={refresh}>
            <RefreshCw size={18} aria-hidden="true" />
            Try again
          </button>
        </>
      )}

      {!loading && !error && total === 0 && (
        <div className={styles.emptyState}>
          <div className={styles.emptyTitle}>No deliveries assigned</div>
          <div>Deliveries scheduled for today will appear here.</div>
        </div>
      )}

      {!loading && !error && total > 0 && (
        <>
          <ul className={styles.list}>
            {ordered.map((stop) => (
              <StopCard
                key={stop.paymentid}
                stop={stop}
                status={statusFor(stop)}
                onOpen={() => openStop(stop)}
              />
            ))}
          </ul>

          <button
            type="button"
            className={styles.ghostBtn}
            onClick={refresh}
            disabled={refreshing}
          >
            <RefreshCw size={18} aria-hidden="true" />
            {refreshing ? "Refreshing…" : "Refresh route"}
          </button>
        </>
      )}
    </DriverScreen>
  )
}
